import { Main } from "../Main";
import { TextureLoader, RepeatWrapping, DoubleSide, MeshStandardMaterial, Material, FrontSide, BackSide, CubeTextureLoader, CubeReflectionMapping, Texture, CubeTexture, RGBAFormat, SRGBColorSpace} from "three";

/**
 * todo move this from Singleton to be Building's property
 * todo move other building materials here
 */

export class MaterialManager
{
	private static _config: any;

	private _glassMaterial: MeshStandardMaterial;
	private _textureSky: CubeTexture;

	public normalMap        : Texture;

	public wall             : MeshStandardMaterial;
	public roof             : MeshStandardMaterial;
	public roof_singleSided : MeshStandardMaterial;
	public floor            : MeshStandardMaterial;
	public interior         : MeshStandardMaterial;
	public wallColor        : MeshStandardMaterial;

	public trim             : MeshStandardMaterial;
	public trim_roofColor   : MeshStandardMaterial;
	public wall_doubleSided : MeshStandardMaterial;

	public addons: {
		door: MeshStandardMaterial[];
		window: Material[];
		rollup: MeshStandardMaterial[];
		rollup_frame: MeshStandardMaterial[];
	};

	private static _instance:MaterialManager;
	public static getInstance(config?: any):MaterialManager
	{
		MaterialManager._config = config;

		return MaterialManager._instance || (MaterialManager._instance = new MaterialManager());
	}

	constructor()
	{
		this.init();
	}

	// ======================================================================================================
	// Init

	private init()
	{
		const path = `assets/skybox/sunnysky/`;
		const format = '.jpg';
		const urls = [
			path + 'px' + format, path + 'nx' + format,
			path + 'py' + format, path + 'ny' + format,
			path + 'pz' + format, path + 'nz' + format
		];

		this._textureSky = new CubeTextureLoader().load(urls);
		this._textureSky.format = RGBAFormat;
		this._textureSky.mapping = CubeReflectionMapping;

		this.createBuildingMaterials();
		this.createTrim();
		this.createRoofColor();
		this.createWallDoubleSided();
		this.createAddons();

		this.addListeners();
	}

	private createBuildingMaterials()
	{
		const normalMap = new TextureLoader().load("assets/building/wall_panel_normal.png", () => {
			Main.getInstance().render = true;
		});
		normalMap.wrapS = normalMap.wrapT = RepeatWrapping;
		this.normalMap = normalMap;

		const colorsConfig = Main.getInstance().config.colors;

		// wall

		const wallColor = colorsConfig.wall.value;

		const wallMaterial = new MeshStandardMaterial({
			//mapX: normalMap,
			normalMap: normalMap,
			shadowSide: DoubleSide,
			name: "wall",
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 0.8,
		});
		wallMaterial.color.set(wallColor);

		// roof

		const roofColor = colorsConfig.roof.value;
		const roofMaterial = new MeshStandardMaterial({
			//mapX: normalMap,
			normalMap: normalMap,
			//normalScale: new Vector2(-1, -1),
			color: roofColor,
			shadowSide: DoubleSide,
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 1,
		});
		roofMaterial.side = DoubleSide;

		this.roof_singleSided = roofMaterial.clone();
		this.roof_singleSided.side = FrontSide;

		// floor

		const loader = new TextureLoader();
		const concrete = loader.load("assets/ground/concrete.jpg", () =>
		{
			Main.getInstance().render = true;
		});
		concrete.colorSpace = SRGBColorSpace;
		concrete.wrapS = concrete.wrapT = RepeatWrapping;
		concrete.repeat.set(7.5, 7.5);

		//const interiorColor = 0xdbe4e6; //0xFFFFFF;
		const floorMaterial = new MeshStandardMaterial({
			color: 0xc5cdcf,
			map: concrete,
			envMap: this._textureSky,
			metalness: 0,
			roughness: 1,
		});
		//floorMaterial.ambient.set("#ffffff");
		floorMaterial.side = BackSide;

		// interior (same as wall but white)

		const interiorMaterial = new MeshStandardMaterial({
			//mapX: normalMap,
			normalMap: normalMap,
			//normalScale: new Vector2(-1, -1),
			color: 0xdbe4e6,
			opacity: 0.5,
			name: "interior",
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 0.8,
		});
		interiorMaterial.side = BackSide;

		const wallColorMaterial = new MeshStandardMaterial({
			shadowSide: DoubleSide,
			name: "wallColor",
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 1,
		});
		wallColorMaterial.color.set(wallColor);

		this.wall      = wallMaterial;
		this.roof      = roofMaterial;
		this.floor     = floorMaterial;
		this.interior  = interiorMaterial;
		this.wallColor = wallColorMaterial;
	}

	public setWallTransparency(value: boolean)
	{
		this.wall.transparent = value;
		this.interior.transparent = value;
		this.wall.opacity = this.interior.opacity = value ? 0.5 : 1;

		// TODO: material.needsUpdate won't be needed hopefully, once this bug is resolved in three.js:
		// https://github.com/mrdoob/three.js/issues/23866
		// It seems this bug was introduced to three.js in r137
		this.wall.needsUpdate = true;
		this.interior.needsUpdate = true;

		Main.getInstance().render = true;
	}

	private createTrim()
	{
		const trimColorConfig = Main.getInstance().config.colors.trim;

		const trim = new MeshStandardMaterial({
			flatShading: true,
			shadowSide: DoubleSide,
			side: DoubleSide,
			color: trimColorConfig.value,
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 1,
		});
		trimColorConfig.signal.add(this.onTrimColorChange);
		//this.onTrimColorChange();
		// todo cleanup material: starting color is hex, then it becomes number on change signal

		this.trim = trim;
	}

	private createRoofColor()
	{
		const roofColorConfig = Main.getInstance().config.colors.roof;

		const trim_roofColor = new MeshStandardMaterial({
			flatShading: true,
			side: DoubleSide,
			shadowSide: DoubleSide,
			color: roofColorConfig.value,
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 0.8,
		});

		this.trim_roofColor = trim_roofColor;
	}

	private createWallDoubleSided()
	{
		// wall_doubleSided

		const wall_doubleSided = this.wall.clone();
		wall_doubleSided.side = DoubleSide;

		this.wall_doubleSided = wall_doubleSided;
	}

	private createAddons()
	{
		this.addons = {
			door: this.createAddonDoorMaterial(),
			window: this.createAddonWindowMaterial(),
			rollup: this.createAddonRollUpMaterial(),
			rollup_frame: this.createAddonRollUpMaterial(true)
		};
	}

	private createAddonDoorMaterial(): MeshStandardMaterial[]
	{
		const white = new MeshStandardMaterial({
			color: 0xdbe4e6,
			flatShading: true,
			shadowSide: DoubleSide,
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 0.8,
		});

		//const color = 0xdbe4e6;
		//this._whiteMaterial.ambient.set(color);

		const wall = [
			this.trim,
			white
		];

		return wall;
	}

	private createAddonWindowMaterial(): Material[]
	{
		this._glassMaterial = new MeshStandardMaterial({
			color: 0xdbe4e6,
			transparent: true,
			opacity: 0.4,
			roughness: 0.8,
			metalness: 0.8,
			envMap: this._textureSky,//Main.getInstance().envMapsToggler.checked ? this._textureSky : null,
		});

		const wall = [
			this.trim,
			this._glassMaterial
		];

		return wall;
	}

	public toggleEnvMap(value:boolean)
	{
		if (value)
		{
			this._glassMaterial.envMap = this._textureSky;
		}
		else
		{
			this._glassMaterial.envMap = null;
		}
		this._glassMaterial.needsUpdate = true;
	}

	private createAddonRollUpMaterial(frame = false)
	{
		const signalConfig = Main.getInstance().config.colors.rollUp;
		signalConfig.signal.add(this.onRollUpColorChange);
		const color = signalConfig.value;

		const outerMaterial = new MeshStandardMaterial({
			color: color,
			flatShading: true,
			side: DoubleSide,
			shadowSide: DoubleSide,
			envMap: this._textureSky,
			metalness: 0.05,
			roughness: 0.8,
		});

		const innerMaterial = outerMaterial.clone();

		if (frame)
		{
			outerMaterial.visible = false;
			innerMaterial.visible = false;
		}

		const rollup = [
			this.trim,
			outerMaterial,
			innerMaterial
		];

		return rollup;
	}

	private addListeners()
	{
		const colorConfig = Main.getInstance().config.colors;

		const roofColorConfig = colorConfig.roof;
		roofColorConfig.signal.add(this.onRoofColorChange);

		const wallColorConfig = colorConfig.wall;
		wallColorConfig.signal.add(this.onWallColorChange);
	}

	// ======================================================================================================
	// WallColor

	private onTrimColorChange = () =>
	{
		const trimConfig = Main.getInstance().config.colors.trim;
		this.trim.color.set(trimConfig.value);
	};

	private onWallColorChange = (value:number) =>
	{
		this.wall.color.set(value);

		const wallColorConfig = Main.getInstance().config.colors.wall;
		this.wall_doubleSided.color.set(wallColorConfig.value);

		this.wallColor.color.set(wallColorConfig.value);

		Main.getInstance().render = true;
	};

	private onRoofColorChange = (value:number) =>
	{
		this.roof.color.set(value);
		this.roof_singleSided.color.set(value);

		const roofColorConfig = Main.getInstance().config.colors.roof;
		this.trim_roofColor.color.set(roofColorConfig.value);
	};

	private onRollUpColorChange = (value:number) =>
	{
		const rollUpMaterial = <MeshStandardMaterial>this.addons.rollup[1];
		rollUpMaterial.color.set(value);
	}
}
